import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  GlobeAltIcon,
  Cog6ToothIcon,
  ClockIcon,
  ShieldCheckIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  BuildingOfficeIcon,
  UsersIcon,
  XMarkIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  NoSymbolIcon,
} from '../../icons';
import { useTheme } from '../../contexts/ThemeContext';
import { adminAPI } from '../../services/adminAPI';
import { getRoleLabel } from '../../utils/adminRoles';

const TABS = [
  { id: 'languages', label: 'Languages', icon: GlobeAltIcon },
  { id: 'users', label: 'User & Role Management', icon: UsersIcon },
  { id: 'platform', label: 'Platform', icon: Cog6ToothIcon },
];

const LANGUAGE_SEED = [
  { id: 'en', name: 'English', native: 'English', code: 'en', enabled: true, isDefault: true, addedOn: '12 Jan 2024', updatedOn: '02 Mar 2026' },
  { id: 'tw', name: 'Twi', native: 'Twi', code: 'tw', enabled: true, isDefault: false, addedOn: '12 Jan 2024', updatedOn: '28 Feb 2026' },
  { id: 'ee', name: 'Ewe', native: 'Eʋegbe', code: 'ee', enabled: true, isDefault: false, addedOn: '15 Mar 2024', updatedOn: '12 Feb 2026' },
  { id: 'ha', name: 'Hausa', native: 'Hausa', code: 'ha', enabled: true, isDefault: false, addedOn: '02 Apr 2024', updatedOn: '05 Mar 2026' },
  { id: 'ga', name: 'Ga', native: 'Ga', code: 'ga', enabled: true, isDefault: false, addedOn: '15 Mar 2024', updatedOn: '20 Feb 2026' },
  { id: 'fr', name: 'French', native: 'Français', code: 'fr', enabled: true, isDefault: false, addedOn: '12 Jan 2024', updatedOn: '18 Feb 2026' },
  { id: 'zh', name: 'Chinese', native: '中文', code: 'zh', enabled: true, isDefault: false, addedOn: '20 Feb 2024', updatedOn: '01 Mar 2026' },
  // { id: 'dag', name: 'Dagbani', native: 'Dagbani', code: 'dag', enabled: false, isDefault: false, addedOn: '02 Apr 2024', updatedOn: '05 Mar 2026' },
  // { id: 'fat', name: 'Fante', native: 'Fante', code: 'fat', enabled: false, isDefault: false, addedOn: '02 Apr 2024', updatedOn: '05 Mar 2026' },
];

const PLATFORM_ROLE_SEED = [
  {
    id: 'role-super_admin',
    name: 'Super Admin',
    key: 'super_admin',
    description: 'Full platform access across all tenants',
    permissions: ['Manage tenants', 'Global setups', 'Audit log', 'Regeneration', 'User management'],
    usersCount: 0,
    createdOn: '12 Jan 2024',
  },
  {
    id: 'role-court_admin',
    name: 'Court Admin',
    key: 'court_admin',
    description: 'Administer a single court tenant',
    permissions: ['Manage users', 'Tenant setups', 'Approvals', 'Audit log'],
    usersCount: 0,
    createdOn: '12 Jan 2024',
  },
  {
    id: 'role-registrar',
    name: 'Registrar',
    key: 'registrar',
    description: 'Capture and manage court sessions',
    permissions: ['Record', 'Upload', 'Edit drafts', 'Export'],
    usersCount: 0,
    createdOn: '12 Jan 2024',
  },
  {
    id: 'role-judge',
    name: 'Judge',
    key: 'judge',
    description: 'Review and certify transcripts',
    permissions: ['Review', 'Certify', 'Export'],
    usersCount: 0,
    createdOn: '18 Feb 2024',
  },
  {
    id: 'role-reviewer',
    name: 'Reviewer',
    key: 'reviewer',
    description: 'Review draft transcripts',
    permissions: ['Review', 'Comment', 'Export'],
    usersCount: 0,
    createdOn: '02 Apr 2024',
  },
];

const PERMISSION_GROUPS = [
  { label: 'Platform', items: ['Manage tenants', 'User management', 'Global setups', 'Audit log', 'Regeneration'] },
  { label: 'Court Admin', items: ['Approvals', 'Tenant setups', 'Manage users'] },
  { label: 'Operations', items: ['Record', 'Upload', 'Edit drafts', 'Review', 'Certify', 'Comment', 'Export'] },
];

const NestedCard = ({ title, subtitle, children, className = '', actions }) => (
  <div className={`md-card border border-matdash-border shadow-none h-full ${className}`.trim()}>
    <div className="md-card-body p-5">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          {title && <h4 className="text-base font-semibold text-matdash-dark mb-1">{title}</h4>}
          {subtitle && <p className="md-card-subtitle mb-0">{subtitle}</p>}
        </div>
        {actions}
      </div>
      {children}
    </div>
  </div>
);

const ToggleSwitch = ({ checked, onChange, id, disabled = false }) => (
  <button
    type="button"
    id={id}
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    className={`md-switch ${checked ? 'md-switch-track-on' : 'md-switch-track-off'} ${
      disabled ? 'opacity-50 cursor-not-allowed' : ''
    }`}
  >
    <span className={`md-switch-thumb ${checked ? 'translate-x-4' : 'translate-x-0'}`} />
  </button>
);

const KpiMini = ({ icon: Icon, color, bg, label, value, hint }) => (
  <div className="md-card border border-matdash-border shadow-none">
    <div className="md-card-body p-4 flex items-center gap-3">
      <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: bg }}>
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <div>
        <p className="text-xs font-medium text-matdash-muted mb-0">{label}</p>
        <p className="text-xl font-bold text-matdash-dark mb-0 leading-tight">{value}</p>
        {hint && <p className="text-xs text-matdash-muted mb-0">{hint}</p>}
      </div>
    </div>
  </div>
);

const PermissionChips = ({ permissions, expanded, onToggle, limit = 3 }) => {
  const needsToggle = permissions.length > limit;
  const shown = expanded || !needsToggle ? permissions : permissions.slice(0, limit);
  return (
    <div>
      <div className="flex flex-wrap gap-1.5">
        {shown.map((p) => (
          <span key={p} className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium bg-matdash-primary-subtle text-matdash-primary">
            {p}
          </span>
        ))}
      </div>
      {needsToggle && (
        <button type="button" onClick={onToggle} className="mt-1.5 text-xs font-medium text-matdash-primary hover:underline">
          {expanded ? 'View less' : `View more (+${permissions.length - limit})`}
        </button>
      )}
    </div>
  );
};

const PermissionMatrix = ({ selected, onChange, dark }) => (
  <div className="space-y-4">
    {PERMISSION_GROUPS.map((group) => (
      <div key={group.label}>
        <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${dark ? 'text-gray-400' : 'text-matdash-muted'}`}>
          {group.label}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {group.items.map((perm) => {
            const checked = selected.includes(perm);
            return (
              <label
                key={perm}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer ${
                  checked
                    ? 'border-matdash-primary bg-matdash-primary-subtle text-matdash-dark'
                    : dark
                      ? 'border-gray-700 text-gray-300'
                      : 'border-matdash-border text-matdash-dark'
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => {
                    onChange(
                      checked ? selected.filter((p) => p !== perm) : [...selected, perm],
                    );
                  }}
                  className="rounded border-gray-300 text-matdash-primary focus:ring-matdash-primary"
                />
                {perm}
              </label>
            );
          })}
        </div>
      </div>
    ))}
  </div>
);

const CreateRoleModal = ({ open, onClose, onSubmit, dark }) => {
  const [form, setForm] = useState({ name: '', description: '' });

  useEffect(() => {
    if (open) setForm({ name: '', description: '' });
  }, [open]);

  if (!open) return null;

  const inputCls = `w-full px-3 py-2.5 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-matdash-primary/40
    ${dark ? 'bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900'}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <form
        onSubmit={(e) => { e.preventDefault(); onSubmit(form); }}
        className={`w-full max-w-lg rounded-2xl shadow-xl ${dark ? 'bg-gray-900' : 'bg-white'}`}
      >
        <div className={`flex items-start justify-between px-6 py-5 border-b ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-full bg-matdash-primary flex items-center justify-center flex-shrink-0">
              <ShieldCheckIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className={`text-lg font-bold mb-0 ${dark ? 'text-gray-50' : 'text-matdash-dark'}`}>Create Role</h2>
              <p className={`text-sm mt-0.5 ${dark ? 'text-gray-400' : 'text-matdash-muted'}`}>
                Define a new platform role.
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-matdash-muted hover:text-matdash-dark p-1" aria-label="Close">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-matdash-dark mb-1">
              Role Name <span className="text-red-500">*</span>
            </label>
            <input
              required
              placeholder="e.g. Regional Supervisor"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-matdash-dark mb-1">Description</label>
            <textarea
              rows={3}
              placeholder="What this role is responsible for"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className={inputCls}
            />
          </div>
        </div>

        <div className={`flex justify-end gap-2 px-6 py-4 border-t ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
          <button type="button" onClick={onClose} className="md-btn md-btn-outline md-btn-sm">Cancel</button>
          <button type="submit" className="md-btn md-btn-primary md-btn-sm inline-flex items-center gap-1.5">
            <PlusIcon className="h-4 w-4" />
            Create Role
          </button>
        </div>
      </form>
    </div>
  );
};

const AddPlatformUserModal = ({ open, onClose, onSubmit, roles, dark }) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    permissions: [],
  });

  useEffect(() => {
    if (open) {
      setForm({
        name: '',
        email: '',
        phone: '',
        role: roles[0]?.key || '',
        permissions: roles[0]?.permissions ? [...roles[0].permissions] : [],
      });
    }
  }, [open, roles]);

  if (!open) return null;

  const inputCls = `w-full px-3 py-2.5 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-matdash-primary/40
    ${dark ? 'bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900'}`;

  const handleRoleChange = (roleKey) => {
    const role = roles.find((r) => r.key === roleKey);
    setForm((prev) => ({
      ...prev,
      role: roleKey,
      permissions: role?.permissions ? [...role.permissions] : [],
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <form
        onSubmit={(e) => { e.preventDefault(); onSubmit(form); }}
        className={`w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-xl ${dark ? 'bg-gray-900' : 'bg-white'}`}
      >
        <div className={`flex items-start justify-between px-6 py-5 border-b sticky top-0 z-10 ${dark ? 'border-gray-800 bg-gray-900' : 'border-matdash-border bg-white'}`}>
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-full bg-matdash-primary flex items-center justify-center flex-shrink-0">
              <UsersIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className={`text-lg font-bold mb-0 ${dark ? 'text-gray-50' : 'text-matdash-dark'}`}>Add User</h2>
              <p className={`text-sm mt-0.5 ${dark ? 'text-gray-400' : 'text-matdash-muted'}`}>
                Provision a platform user and assign role permissions.
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-matdash-muted hover:text-matdash-dark p-1" aria-label="Close">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-matdash-dark mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                required
                placeholder="Full name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-matdash-dark mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                required
                type="email"
                placeholder="name@jog.gov.gh"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-matdash-dark mb-1">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                required
                type="tel"
                placeholder="024 000 0000"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className={inputCls}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-matdash-dark mb-1">
                Role <span className="text-red-500">*</span>
              </label>
              <select
                required
                value={form.role}
                onChange={(e) => handleRoleChange(e.target.value)}
                className={inputCls}
              >
                {roles.map((r) => (
                  <option key={r.id} value={r.key}>{r.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-matdash-dark mb-2">Permission matrix</label>
            <p className="text-xs text-matdash-muted mb-3">
              Defaults follow the selected role. Adjust individual permissions for this user.
            </p>
            <PermissionMatrix
              selected={form.permissions}
              onChange={(permissions) => setForm({ ...form, permissions })}
              dark={dark}
            />
          </div>
        </div>

        <div className={`flex justify-end gap-2 px-6 py-4 border-t sticky bottom-0 ${dark ? 'border-gray-800 bg-gray-900' : 'border-matdash-border bg-white'}`}>
          <button type="button" onClick={onClose} className="md-btn md-btn-outline md-btn-sm">Cancel</button>
          <button type="submit" className="md-btn md-btn-primary md-btn-sm inline-flex items-center gap-1.5">
            <PlusIcon className="h-4 w-4" />
            Add User
          </button>
        </div>
      </form>
    </div>
  );
};

const AddLanguageModal = ({ open, onClose, onSubmit, dark }) => {
  const [form, setForm] = useState({
    name: '',
    native: '',
    code: '',
    enabled: true,
    isDefault: false,
  });

  useEffect(() => {
    if (open) {
      setForm({ name: '', native: '', code: '', enabled: true, isDefault: false });
    }
  }, [open]);

  if (!open) return null;

  const inputCls = `w-full px-3 py-2.5 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-matdash-primary/40
    ${dark ? 'bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900'}`;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <form
        onSubmit={handleSubmit}
        className={`w-full max-w-lg rounded-2xl shadow-xl overflow-hidden ${dark ? 'bg-gray-900' : 'bg-white'}`}
      >
        <div className={`flex items-start justify-between px-6 py-5 border-b ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-full bg-matdash-primary flex items-center justify-center flex-shrink-0">
              <GlobeAltIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className={`text-lg font-bold mb-0 ${dark ? 'text-gray-50' : 'text-matdash-dark'}`}>Add New Language</h2>
              <p className={`text-sm mt-0.5 ${dark ? 'text-gray-400' : 'text-matdash-muted'}`}>
                Register a transcription language available to all court tenants.
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-matdash-muted hover:text-matdash-dark p-1" aria-label="Close">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-matdash-dark mb-1">
              Language Name <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-matdash-muted mb-2">Official English name of the language.</p>
            <input
              required
              placeholder="e.g. Twi"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={inputCls}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-matdash-dark mb-1">
                Native Name <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-matdash-muted mb-2">Name in the language itself.</p>
              <input
                required
                placeholder="e.g. Dagbanli"
                value={form.native}
                onChange={(e) => setForm({ ...form, native: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-matdash-dark mb-1">
                Language Code <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-matdash-muted mb-2">ISO-style short code.</p>
              <input
                required
                placeholder="e.g. dag"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase() })}
                className={inputCls}
                maxLength={8}
              />
            </div>
          </div>

          <div className={`rounded-xl border p-4 space-y-3 ${dark ? 'border-gray-700' : 'border-matdash-border'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-matdash-dark mb-0">Enable immediately</p>
                <p className="text-xs text-matdash-muted mb-0">Make available to all tenants now</p>
              </div>
              <ToggleSwitch
                id="new-lang-enabled"
                checked={form.enabled}
                onChange={(v) => setForm({ ...form, enabled: v })}
              />
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-matdash-border">
              <div>
                <p className="text-sm font-medium text-matdash-dark mb-0">Set as platform default</p>
                <p className="text-xs text-matdash-muted mb-0">Used when no language is selected</p>
              </div>
              <ToggleSwitch
                id="new-lang-default"
                checked={form.isDefault}
                onChange={(v) => setForm({ ...form, isDefault: v })}
              />
            </div>
          </div>
        </div>

        <div className={`flex justify-end gap-2 px-6 py-4 border-t ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
          <button type="button" onClick={onClose} className="md-btn md-btn-outline md-btn-sm">Cancel</button>
          <button type="submit" className="md-btn md-btn-primary md-btn-sm inline-flex items-center gap-1.5">
            <PlusIcon className="h-4 w-4" />
            Add Language
          </button>
        </div>
      </form>
    </div>
  );
};

const SuperAdminSettings = () => {
  const { dark } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab = TABS.some((t) => t.id === tabParam) ? tabParam : 'languages';

  const [languages, setLanguages] = useState(LANGUAGE_SEED);
  const [langQuery, setLangQuery] = useState('');
  const [langStatus, setLangStatus] = useState('all');
  const [showAddLanguage, setShowAddLanguage] = useState(false);
  const [roles, setRoles] = useState(PLATFORM_ROLE_SEED);
  const [platformUsers, setPlatformUsers] = useState([]);
  const [expandedRoleIds, setExpandedRoleIds] = useState({});
  const [showCreateRole, setShowCreateRole] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [tenants, setTenants] = useState([]);

  const setTab = (id) => {
    const next = id === 'languages' ? {} : { tab: id };
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (searchParams.get('add') === 'language') {
      setShowAddLanguage(true);
      setTab('languages');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const load = async () => {
      try {
        const langResp = await adminAPI.getPlatformLanguages();
        const rows = langResp.data?.data || langResp.data || [];
        if (Array.isArray(rows) && rows.length) {
          setLanguages(rows.map((row) => ({
            id: row.id || row.code,
            name: row.name || row.label,
            native: row.native || row.native_name || row.name || row.label,
            code: row.code,
            enabled: row.enabled !== false,
            isDefault: Boolean(row.isDefault || row.is_default),
            addedOn: row.addedOn || '—',
            updatedOn: row.updatedOn || '—',
          })));
        }
      } catch {
        /* keep seed until platform catalog is available */
      }
      try {
        const tenantsResp = await adminAPI.getTenants();
        const list = tenantsResp.data?.data || tenantsResp.data || [];
        setTenants(list);
      } catch {
        /* ignore */
      }
      try {
        const usersResp = await adminAPI.getPlatformUsers();
        const rows = usersResp.data?.data || usersResp.data || [];
        setPlatformUsers(rows.map((row) => ({
          id: row.id || row.user_id,
          name: row.full_name || row.name || row.email,
          email: row.email,
          phone: row.phone || '—',
          role: row.role,
          status: row.status || (row.is_active ? 'active' : 'inactive'),
        })));
      } catch {
        setPlatformUsers([]);
      }
    };
    load();
  }, []);

  const filteredLanguages = useMemo(() => {
    return languages.filter((lang) => {
      const q = langQuery.trim().toLowerCase();
      const matchesQuery = !q
        || lang.name.toLowerCase().includes(q)
        || lang.native.toLowerCase().includes(q)
        || lang.code.toLowerCase().includes(q);
      const matchesStatus = langStatus === 'all'
        || (langStatus === 'enabled' && lang.enabled)
        || (langStatus === 'disabled' && !lang.enabled);
      return matchesQuery && matchesStatus;
    });
  }, [languages, langQuery, langStatus]);

  const enabledCount = languages.filter((l) => l.enabled).length;
  const disabledCount = languages.length - enabledCount;

  const toggleLanguage = async (id) => {
    const current = languages.find((l) => l.id === id);
    if (!current) return;
    if (current.isDefault && current.enabled) {
      toast.error('The default language cannot be deactivated');
      return;
    }
    const nextEnabled = !current.enabled;
    setLanguages((prev) => prev.map((l) => (l.id === id ? { ...l, enabled: nextEnabled } : l)));
    try {
      const resp = await adminAPI.updatePlatformLanguage(current.code || id, { enabled: nextEnabled });
      const row = resp.data?.data || resp.data || {};
      setLanguages((prev) => prev.map((l) => (
        l.id === id
          ? {
              ...l,
              enabled: row.enabled !== false && row.enabled !== undefined ? row.enabled : nextEnabled,
              updatedOn: row.updatedOn || l.updatedOn,
            }
          : l
      )));
      toast.success(nextEnabled ? `${current.name} enabled platform-wide` : `${current.name} deactivated platform-wide`);
    } catch (err) {
      setLanguages((prev) => prev.map((l) => (l.id === id ? { ...l, enabled: current.enabled } : l)));
      toast.error(err.response?.data?.error || err.response?.data?.message || 'Failed to update language');
    }
  };

  const handleAddLanguage = (form) => {
    const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const id = form.code || `lang-${Date.now()}`;
    setLanguages((prev) => {
      const next = form.isDefault
        ? prev.map((l) => ({ ...l, isDefault: false }))
        : prev;
      return [
        ...next,
        {
          id,
          name: form.name,
          native: form.native,
          code: form.code,
          enabled: form.enabled,
          isDefault: form.isDefault,
          addedOn: today,
          updatedOn: today,
        },
      ];
    });
    setShowAddLanguage(false);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete('add');
      return p;
    }, { replace: true });
    toast.success(`${form.name} added`);
  };

  const handleCreateRole = (form) => {
    const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const key = form.name.trim().toLowerCase().replace(/\s+/g, '_');
    setRoles((prev) => [
      ...prev,
      {
        id: `role-${Date.now()}`,
        name: form.name.trim(),
        key,
        description: form.description.trim() || 'Custom platform role',
        permissions: [],
        usersCount: 0,
        createdOn: today,
      },
    ]);
    setShowCreateRole(false);
    toast.success(`Role “${form.name.trim()}” created`);
  };

  const handleAddPlatformUser = (form) => {
    setPlatformUsers((prev) => [
      ...prev,
      {
        id: `pu-${Date.now()}`,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        role: form.role,
        permissions: form.permissions,
        status: 'active',
      },
    ]);
    setRoles((prev) => prev.map((r) => (
      r.key === form.role ? { ...r, usersCount: (r.usersCount || 0) + 1 } : r
    )));
    setShowAddUser(false);
    toast.success(`${form.name.trim()} added`);
  };

  const openAddLanguage = () => {
    setShowAddLanguage(true);
    setSearchParams({ tab: 'languages', add: 'language' }, { replace: true });
  };

  const closeAddLanguage = () => {
    setShowAddLanguage(false);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete('add');
      if (p.get('tab') === 'languages') p.delete('tab');
      return p;
    }, { replace: true });
  };

  return (
    <>
      <div className={`md-card overflow-hidden ${dark ? '!bg-gray-900 !border-gray-800' : ''}`}>
        <div className="flex flex-col md:flex-row min-h-[560px]">
          {/* Vertical left tabs */}
          <nav
            className={`md:w-56 lg:w-64 shrink-0 border-b md:border-b-0 md:border-r p-3 space-y-1
              ${dark ? 'border-gray-800 bg-gray-900' : 'border-matdash-border bg-gray-50/60'}`}
            role="tablist"
            aria-orientation="vertical"
          >
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setTab(tab.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium text-left transition-colors
                    ${isActive
                      ? 'bg-matdash-primary text-white shadow-sm'
                      : dark
                        ? 'text-gray-300 hover:bg-gray-800'
                        : 'text-matdash-muted hover:bg-white hover:text-matdash-dark'}`}
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>

          <div className="flex-1 min-w-0 p-5">
            {activeTab === 'languages' && (
              <div className="space-y-4">
                <div className="md-alert md-alert-success flex items-center gap-2">
                  <CheckCircleIcon className="h-4 w-4 shrink-0" />
                  Changes are applied instantly across all tenants.
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                  <KpiMini icon={GlobeAltIcon} bg="#FDF0E6" color="#bf5d09" label="Total Languages" value={languages.length} hint="Supported languages" />
                  <KpiMini icon={CheckCircleIcon} bg="#E6FFFA" color="#13DEB9" label="Enabled Languages" value={enabledCount} hint="Active and available" />
                  <KpiMini icon={ClockIcon} bg="#FFF6E5" color="#FFAE1F" label="Disabled Languages" value={disabledCount} hint="Currently disabled" />
                  <KpiMini icon={BuildingOfficeIcon} bg="#F3E8FF" color="#7B61FF" label="Applied To Tenants" value={tenants.length || 0} hint="All active court tenants" />
                </div>

                <NestedCard
                  title="Supported Languages"
                  subtitle="Manage transcription languages available across all court tenants."
                  actions={null
                    /* Add New Language is disabled so only the supported set is offered.
                    <button
                      type="button"
                      onClick={openAddLanguage}
                      className="md-btn md-btn-primary md-btn-sm inline-flex items-center gap-1"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Add New Language
                    </button>
                    */
                  }
                >
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <div className="relative flex-1 min-w-[180px] max-w-xs">
                      <MagnifyingGlassIcon className="h-4 w-4 text-matdash-muted absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        value={langQuery}
                        onChange={(e) => setLangQuery(e.target.value)}
                        placeholder="Search languages..."
                        className="md-form-control pl-9"
                      />
                    </div>
                    <select
                      value={langStatus}
                      onChange={(e) => setLangStatus(e.target.value)}
                      className="md-form-select max-w-[160px]"
                    >
                      <option value="all">All Status</option>
                      <option value="enabled">Enabled</option>
                      <option value="disabled">Disabled</option>
                    </select>
                  </div>

                  <div className="overflow-x-auto -mx-1">
                    <table className="md-table min-w-full">
                      <thead>
                        <tr>
                          <th>Language</th>
                          <th>Native Name</th>
                          <th>Code</th>
                          <th>Status</th>
                          <th>Default</th>
                          <th>Added On</th>
                          <th>Last Updated</th>
                          {/* <th>Actions</th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLanguages.map((lang) => (
                          <tr key={lang.id}>
                            <td><span className="font-medium text-matdash-dark">{lang.name}</span></td>
                            <td className="text-matdash-muted">{lang.native}</td>
                            <td>
                              <span className="inline-flex px-2 py-0.5 rounded bg-matdash-body text-matdash-muted text-xs font-medium">
                                {lang.code}
                              </span>
                            </td>
                            <td>
                              <ToggleSwitch
                                id={`lang-${lang.id}`}
                                checked={lang.enabled}
                                disabled={lang.isDefault}
                                onChange={() => toggleLanguage(lang.id)}
                              />
                            </td>
                            <td>
                              {lang.isDefault ? (
                                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">Default</span>
                              ) : (
                                <span className="text-matdash-muted text-xs">—</span>
                              )}
                            </td>
                            <td className="text-matdash-muted text-xs">{lang.addedOn}</td>
                            <td className="text-matdash-muted text-xs">{lang.updatedOn}</td>
                            {/* <td>
                              <button type="button" className="text-sm text-matdash-primary hover:underline">Edit</button>
                            </td> */}
                          </tr>
                        ))}
                        {filteredLanguages.length === 0 && (
                          <tr>
                            <td colSpan={7} className="text-center text-matdash-muted py-6">No languages match your filters.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-matdash-muted mt-3 mb-0">
                    Showing {filteredLanguages.length} of {languages.length} languages
                  </p>
                </NestedCard>
              </div>
            )}

            {activeTab === 'users' && (
              <div className="space-y-4">
                <NestedCard
                  title="Platform roles"
                  subtitle="Roles that have been created for the platform."
                  actions={
                    <button
                      type="button"
                      onClick={() => setShowCreateRole(true)}
                      className="md-btn md-btn-primary md-btn-sm inline-flex items-center gap-1"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Create Role
                    </button>
                  }
                >
                  <div className="overflow-x-auto -mx-1">
                    <table className="md-table min-w-full">
                      <thead>
                        <tr>
                          <th>Role</th>
                          <th>Description</th>
                          <th>Users</th>
                          <th>Created On</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {roles.map((r) => (
                          <tr key={r.id}>
                            <td>
                              <span className="font-medium text-matdash-dark">{r.name}</span>
                            </td>
                            <td className="text-matdash-muted text-sm max-w-xs">{r.description}</td>
                            <td className="text-matdash-muted text-sm">
                              {platformUsers.filter((u) => u.role === r.key).length}
                            </td>
                            <td className="text-matdash-muted text-xs">{r.createdOn}</td>
                            <td>
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  title="View"
                                  aria-label={`View ${r.name}`}
                                  onClick={() => toast.message(r.name, { description: r.description })}
                                  className={`w-8 h-8 inline-flex items-center justify-center rounded-lg border ${
                                    dark ? 'border-gray-700 text-gray-300 hover:bg-gray-700' : 'border-gray-200 text-matdash-primary hover:bg-matdash-primary-subtle'
                                  }`}
                                >
                                  <EyeIcon className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  title="Edit"
                                  aria-label={`Edit ${r.name}`}
                                  onClick={() => toast.message('Edit role', { description: r.name })}
                                  className={`w-8 h-8 inline-flex items-center justify-center rounded-lg border ${
                                    dark ? 'border-gray-700 text-gray-300 hover:bg-gray-700' : 'border-gray-200 text-matdash-muted hover:bg-gray-50'
                                  }`}
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  title="Delete"
                                  aria-label={`Delete ${r.name}`}
                                  onClick={() => {
                                    setRoles((prev) => prev.filter((role) => role.id !== r.id));
                                    toast.success(`${r.name} deleted`);
                                  }}
                                  className={`w-8 h-8 inline-flex items-center justify-center rounded-lg border ${
                                    dark ? 'border-gray-700 text-red-400 hover:bg-gray-700' : 'border-gray-200 text-red-600 hover:bg-red-50'
                                  }`}
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </NestedCard>

                <NestedCard
                  title="Platform Users"
                  subtitle={`${platformUsers.length} users with platform access`}
                  actions={
                    <button
                      type="button"
                      onClick={() => setShowAddUser(true)}
                      className="md-btn md-btn-primary md-btn-sm inline-flex items-center gap-1"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Add User
                    </button>
                  }
                >
                  <div className="overflow-x-auto -mx-1">
                    <table className="md-table min-w-full">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Role</th>
                          <th>Permissions</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {platformUsers.map((u) => (
                          <tr key={u.id}>
                            <td>
                              <span className="font-medium text-matdash-dark">{u.name}</span>
                            </td>
                            <td className="text-matdash-muted text-sm">{u.email}</td>
                            <td className="text-matdash-muted text-sm">{u.phone}</td>
                            <td className="text-matdash-muted text-sm">
                              {roles.find((r) => r.key === u.role)?.name || getRoleLabel(u.role)}
                            </td>
                            <td className="min-w-[180px]">
                              <PermissionChips
                                permissions={u.permissions?.length ? u.permissions : (roles.find((r) => r.key === u.role)?.permissions || [])}
                                expanded={Boolean(expandedRoleIds[`user-${u.id}`])}
                                onToggle={() => setExpandedRoleIds((prev) => ({
                                  ...prev,
                                  [`user-${u.id}`]: !prev[`user-${u.id}`],
                                }))}
                                limit={2}
                              />
                            </td>
                            <td>
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                                u.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                              }`}>
                                {u.status === 'active' ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td>
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  title="View"
                                  aria-label={`View ${u.name}`}
                                  onClick={() => toast.message(u.name, { description: u.email })}
                                  className={`w-8 h-8 inline-flex items-center justify-center rounded-lg border ${
                                    dark ? 'border-gray-700 text-gray-300 hover:bg-gray-700' : 'border-gray-200 text-matdash-primary hover:bg-matdash-primary-subtle'
                                  }`}
                                >
                                  <EyeIcon className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  title="Edit"
                                  aria-label={`Edit ${u.name}`}
                                  onClick={() => toast.message('Edit user', { description: u.name })}
                                  className={`w-8 h-8 inline-flex items-center justify-center rounded-lg border ${
                                    dark ? 'border-gray-700 text-gray-300 hover:bg-gray-700' : 'border-gray-200 text-matdash-muted hover:bg-gray-50'
                                  }`}
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  title="Delete"
                                  aria-label={`Delete ${u.name}`}
                                  onClick={() => {
                                    setPlatformUsers((prev) => prev.filter((user) => user.id !== u.id));
                                    toast.success(`${u.name} deleted`);
                                  }}
                                  className={`w-8 h-8 inline-flex items-center justify-center rounded-lg border ${
                                    dark ? 'border-gray-700 text-red-400 hover:bg-gray-700' : 'border-gray-200 text-red-600 hover:bg-red-50'
                                  }`}
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                                {u.status === 'active' ? (
                                  <button
                                    type="button"
                                    title="Deactivate"
                                    aria-label={`Deactivate ${u.name}`}
                                    onClick={() => {
                                      setPlatformUsers((prev) => prev.map((user) => (
                                        user.id === u.id ? { ...user, status: 'inactive' } : user
                                      )));
                                      toast.success(`${u.name} deactivated`);
                                    }}
                                    className={`w-8 h-8 inline-flex items-center justify-center rounded-lg border ${
                                      dark ? 'border-gray-700 text-red-400 hover:bg-gray-700' : 'border-gray-200 text-red-600 hover:bg-red-50'
                                    }`}
                                  >
                                    <NoSymbolIcon className="h-4 w-4" />
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    title="Activate"
                                    aria-label={`Activate ${u.name}`}
                                    onClick={() => {
                                      setPlatformUsers((prev) => prev.map((user) => (
                                        user.id === u.id ? { ...user, status: 'active' } : user
                                      )));
                                      toast.success(`${u.name} activated`);
                                    }}
                                    className={`w-8 h-8 inline-flex items-center justify-center rounded-lg border ${
                                      dark ? 'border-gray-700 text-emerald-400 hover:bg-gray-700' : 'border-gray-200 text-emerald-600 hover:bg-emerald-50'
                                    }`}
                                  >
                                    <CheckCircleIcon className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                        {platformUsers.length === 0 && (
                          <tr>
                            <td colSpan={7} className="text-center text-matdash-muted py-6">No platform users yet.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </NestedCard>
              </div>
            )}

            {activeTab === 'platform' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <NestedCard title="Platform Defaults" subtitle="Global defaults applied to new court tenants.">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-matdash-dark mb-1">Default audio retention (days)</label>
                      <input type="number" min="1" defaultValue={2555} className="md-form-control" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-matdash-dark mb-1">Default regeneration threshold</label>
                      <input type="number" min="1" defaultValue={3} className="md-form-control" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-matdash-dark mb-1">Default subscription plan</label>
                      <select className="md-form-select" defaultValue="Professional">
                        <option>Enterprise</option>
                        <option>Professional</option>
                        <option>Standard</option>
                      </select>
                    </div>
                    <button type="button" className="md-btn md-btn-primary md-btn-sm" onClick={() => toast.success('Platform defaults saved')}>
                      Save platform defaults
                    </button>
                  </div>
                </NestedCard>

                <NestedCard title="Integrations" subtitle="External services connected to the platform.">
                  <div className="space-y-3">
                    {[
                      { name: 'ASR Compute Cluster', status: 'Connected' },
                      { name: 'Object Storage (Audio)', status: 'Connected' },
                      { name: 'Email Delivery', status: 'Connected' },
                      { name: 'SMS Gateway', status: 'Not configured' },
                    ].map((item) => (
                      <div key={item.name} className="flex items-center justify-between py-2 border-b border-matdash-border last:border-0">
                        <span className="text-sm text-matdash-dark">{item.name}</span>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          item.status === 'Connected' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                        }`}>
                          {item.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </NestedCard>
              </div>
            )}
          </div>
        </div>
      </div>

      <AddLanguageModal
        open={showAddLanguage}
        onClose={closeAddLanguage}
        onSubmit={handleAddLanguage}
        dark={dark}
      />
      <CreateRoleModal
        open={showCreateRole}
        onClose={() => setShowCreateRole(false)}
        onSubmit={handleCreateRole}
        dark={dark}
      />
      <AddPlatformUserModal
        open={showAddUser}
        onClose={() => setShowAddUser(false)}
        onSubmit={handleAddPlatformUser}
        roles={roles}
        dark={dark}
      />
    </>
  );
};

export default SuperAdminSettings;
